import { GUDHUB_SETTINGS } from '../../../../gudhub_settings.js';
import { traceCall } from './callTrace.js';

// The last line of the engine's trace the SIP server has (_sendEngineLog).
const ENGINE_LOG_SENT_KEY = 'gh_engine_log_sent';

//********************* NATIVE CALL ENGINE ********************************//
// Every incoming call runs in the app's native call engine (CallEngine.swift,
// CallEngine.java): SIP, audio and the system call UI all in native code. That
// is what lets a call ring, connect and be talked through with the app closed
// or the phone locked - WKWebView audio cannot coexist with CallKit's audio
// session (Apple DTS: developer.apple.com/forums/thread/767949).
//
// So this WebView neither registers for incoming calls nor carries their
// audio. It gives the engine what it needs to register by itself when a push
// wakes the phone (the push brings the SIP credential), and shows the in-app
// call window for the call the engine is running, its buttons wired back to
// the engine. Android builds from before their native engine answer in this
// WebView instead (nativeCallsMixin.js).
export const nativeEngineMixin = {
    async _hasNativeEngine() {
        const capabilities = await this._safe(() => this._voip()?.getCapabilities());
        return capabilities?.engine === 'native';
    },

    async startNativeEngine() {
        const voip = this._voip();

        await this._safe(() => voip.configure({
            wsUri: GUDHUB_SETTINGS.sip_ws_uri,
            domain: GUDHUB_SETTINGS.sip_domain,
            password: GUDHUB_SETTINGS.sip_digest_shared_secret,
            userId: this.activeUserId,
            // Where a Decline before the call's INVITE goes (the SIP server's
            // POST /push/call/decline) - it has no SIP answer to carry it.
            apiUrl: GUDHUB_SETTINGS.sip_server_url
        }));

        voip.addListener('callStateChanged', (call) => this.renderNativeCall(call));

        // State the WebView missed while suspended or still loading - above
        // all a call answered from the lock screen before the app was opened.
        const sync = async () => this.renderNativeCall(await this._safe(() => voip.getActiveCall()));
        document.addEventListener('visibilitychange', () => {
            if(document.visibilityState !== 'visible') return;
            sync();
            this._sendEngineLog();
        });
        await sync();
        this._sendEngineLog();
    },

    // The engine keeps its own trace across launches (CallEngine's log), and
    // what is new since the last time goes to the SIP server's log ([device]
    // lines, next to the server's own [ringing] ones). A call that went wrong
    // on a locked phone did so with this WebView asleep or not running at
    // all: that trace is the only account of the phone's side of it.
    _sendEngineLog() {
        this._engineLogSending ??= this._sendNewEngineLog().finally(() => {
            this._engineLogSending = null;
        });
        return this._engineLogSending;
    },

    async _sendNewEngineLog() {
        const Preferences = this._plugins().Preferences;
        const diagnostics = await this._safe(() => this._voip().getDiagnostics());
        const log = Array.isArray(diagnostics?.log) ? diagnostics.log : [];
        if(!Preferences || !log.length) return;

        // Everything after the last line sent - all of it once that line has
        // scrolled out of the engine's 300.
        const sent = (await this._safe(() => Preferences.get({ key: ENGINE_LOG_SENT_KEY })))?.value;
        const lines = log.slice(log.lastIndexOf(sent) + 1);
        if(!lines.length) return;

        const { log: _log, ...state } = diagnostics;
        const response = await this._safe(async () => fetch(`${GUDHUB_SETTINGS.sip_server_url}/push/device/log`, {
            method: 'POST',
            headers: await this._authHeaders(),
            body: JSON.stringify({ platform: window.Capacitor.getPlatform(), state, lines })
        }));
        if(response?.ok) await this._safe(() => Preferences.set({ key: ENGINE_LOG_SENT_KEY, value: lines.at(-1) }));
    },

    async renderNativeCall(call) {
        const live = Boolean(call?.callId) && call.state !== 'ended';
        traceCall('nativeEngineCall', { callId: call?.callId, state: call?.state || 'none', reason: call?.reason });

        if(!live) {
            if(this.nativeCallView && (!call?.callId || call.callId === this.nativeCallView)) {
                this.nativeCallView = null;
                this.closeCallDialog();
            }
            return;
        }

        // Until the call is answered, CallKit's own screen is the incoming-call UI.
        if(call.state === 'ringing') {
            this._resolveNativeCallerName(call);
            return;
        }

        this.nativeCallState = call.state === 'connected' ? 'in_call' : 'connecting';
        this.nativeCallMuted = Boolean(call.muted);

        if(this.nativeCallView === call.callId) {
            this.updateDialogState({ callState: this.nativeCallState, isMuted: this.nativeCallMuted });
            return;
        }

        // A call placed from this WebView loses its audio the moment CallKit's
        // call takes the audio session - end it rather than leave it silent.
        if(this.session) this.hangupCall();

        this.nativeCallView = call.callId;
        this.callVideo = false;
        const otherUser = await this._safe(() => gudhub.getUserById(String(call.remoteId)))
            || { fullname: call.remoteName || String(call.remoteId || '') };

        // Ended during the lookup.
        if(this.nativeCallView !== call.callId) return;

        this.closeCallDialog();
        // The latest state, not this event's: another may have arrived during the lookup.
        this.showCallDialog({ otherUser, callState: this.nativeCallState });
        this.updateDialogState({ isMuted: this.nativeCallMuted });
    },

    hangupNativeCall() {
        const callId = this.nativeCallView;
        if(callId) this._safe(() => this._voip().hangup({ callId }));
    },

    toggleNativeMute() {
        const callId = this.nativeCallView;
        if(!callId) return;
        this.nativeCallMuted = !this.nativeCallMuted;
        this.updateDialogState({ isMuted: this.nativeCallMuted });
        this._safe(() => this._voip().setMuted({ callId, muted: this.nativeCallMuted }));
    },

    // The push can only carry the caller's user id; CallKit shows the real
    // name once it has been looked up here.
    async _resolveNativeCallerName(call) {
        if(!call.remoteId || this._namedNativeCall === call.callId) return;
        this._namedNativeCall = call.callId;

        const user = await this._safe(() => gudhub.getUserById(String(call.remoteId)));
        if(user?.fullname && user.fullname !== call.remoteName) {
            await this._safe(() => this._voip().updateCall({ callId: call.callId, callerName: user.fullname }));
        }
    }
};
